import { useEffect, useState } from "react";
import { http, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Save, ShieldCheck, RotateCcw, History, X as XIcon } from "lucide-react";

const emptyHabit = { owner: "adi", name: "", full: 10, partial: 0, partial_label: "", days: "all", position: 0 };

// ---------- Frequency helpers ----------
const WEEK_CODES = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const PRESETS = {
  daily:  "all",
  wk6:    "mon_sat",
  wk5:    "mon,tue,wed,thu,fri",
  wk3:    "mon,wed,fri",
  sunday: "sun_only",
};
const PRESET_LABEL = {
  daily: "Daily (7×/week)",
  wk6:   "6× per week (Mon–Sat)",
  wk5:   "5× per week (Mon–Fri)",
  wk3:   "3× per week (Mon/Wed/Fri)",
  sunday:"Sunday only",
  custom:"Custom…",
};

function daysToPreset(days) {
  const d = (days || "all").toLowerCase();
  for (const [k, v] of Object.entries(PRESETS)) if (v === d) return k;
  return "custom";
}
function customToSet(days) {
  const d = (days || "").toLowerCase();
  if (d === "all")      return new Set(WEEK_CODES);
  if (d === "mon_sat")  return new Set(WEEK_CODES.slice(0, 6));
  if (d === "sun_only") return new Set(["sun"]);
  return new Set(d.split(",").map((s) => s.trim()).filter(Boolean));
}
function setToDaysStr(set) {
  const arr = WEEK_CODES.filter((c) => set.has(c));
  if (arr.length === 7) return "all";
  if (arr.length === 0) return "all";
  return arr.join(",");
}

function FrequencyPicker({ value, onChange, testPrefix }) {
  const preset = daysToPreset(value);
  const [mode, setMode] = useState(preset);
  const [custom, setCustom] = useState(customToSet(value));

  useEffect(() => {
    const next = daysToPreset(value);
    setMode(next);
    setCustom(customToSet(value));
  }, [value]);

  function pickPreset(newMode) {
    setMode(newMode);
    if (newMode === "custom") {
      // Seed custom checkboxes from current value
      const seed = customToSet(value);
      setCustom(seed);
      onChange(setToDaysStr(seed));
    } else {
      onChange(PRESETS[newMode]);
    }
  }
  function toggleDay(code) {
    const next = new Set(custom);
    if (next.has(code)) next.delete(code); else next.add(code);
    setCustom(next);
    onChange(setToDaysStr(next));
  }

  return (
    <div className="flex flex-col gap-2">
      <select className="nb-input py-2 text-sm" value={mode} onChange={(e) => pickPreset(e.target.value)}
              data-testid={`${testPrefix}-freq-preset`}>
        {Object.entries(PRESET_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {mode === "custom" && (
        <div className="flex flex-wrap gap-1" data-testid={`${testPrefix}-freq-custom`}>
          {WEEK_CODES.map((code) => {
            const on = custom.has(code);
            return (
              <button key={code} type="button"
                      onClick={() => toggleDay(code)}
                      className={`nb-btn text-[10px] px-2 py-1 uppercase font-mono tracking-widest ${on ? "bg-adi text-white" : "bg-white"}`}
                      data-testid={`${testPrefix}-freq-day-${code}`}>
                {code}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Backfill Logs (edit past habit completions) ----------
function BackfillLogs() {
  const yesterdayISO = () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  const [user, setUser] = useState("adi");
  const [date, setDate] = useState(yesterdayISO());
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    if (!date) return;
    setLoading(true);
    try {
      const r = await http.get(`/habits`, { params: { owner: user, date } });
      setHabits(r.data.habits || []);
    } catch (e) { toast.error(fmtErr(e)); setHabits([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [user, date]);

  async function setStatus(habit, status) {
    setBusyId(habit.id + status);
    try {
      await http.post(`/habits/${habit.id}/log`, { date, status });
      toast.success(`${habit.name} · ${status.toUpperCase()}  (${new Date(date).toLocaleDateString()})`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusyId(null); }
  }
  async function clearLog(habit) {
    setBusyId(habit.id + "clear");
    try {
      await http.delete(`/admin/logs/${user}/${habit.id}/${date}`);
      toast.success(`Cleared: ${habit.name}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusyId(null); }
  }

  async function markAllFull() {
    if (!habits.length) return;
    if (!window.confirm(`Mark ALL ${habits.length} habits Full for ${user.toUpperCase()} on ${date}?`)) return;
    setBusyId("markall");
    try {
      await Promise.all(habits.map((h) =>
        http.post(`/habits/${h.id}/log`, { date, status: "full" })
      ));
      toast.success(`Marked ${habits.length} habits Full for ${user} on ${date}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusyId(null); }
  }

  const completed = habits.filter((h) => h.log?.status === "full").length;
  const totalPts  = habits.reduce((s, h) => s + (h.log?.points || 0), 0);
  const maxPts    = habits.reduce((s, h) => s + Number(h.full || 0), 0);

  return (
    <div className="nb-card p-6 mb-6" data-testid="backfill-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-ink text-gold flex items-center justify-center">
          <History size={20} strokeWidth={2.5} />
        </div>
        <div>
          <h2 className="font-display font-black text-xl">Edit Past Logs</h2>
          <p className="text-xs text-neutral-600">Fix or backfill a completion after the fact. Points recompute automatically.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Player</label>
          <select className="nb-input mt-1 block py-2" value={user} onChange={(e) => setUser(e.target.value)}
                  data-testid="backfill-user">
            <option value="adi">Adi</option>
            <option value="kudrat">Kudrat</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Date</label>
          <input type="date" className="nb-input mt-1 block py-2" value={date}
                 onChange={(e) => setDate(e.target.value)} data-testid="backfill-date" />
        </div>
        <div className="chip bg-gold" data-testid="backfill-summary">
          {completed}/{habits.length} full · {Math.round(totalPts)}/{Math.round(maxPts)} pts
        </div>
        <button
          type="button"
          onClick={markAllFull}
          disabled={busyId === "markall" || !habits.length}
          className="nb-btn bg-emerald-500 text-white px-3 py-2 text-xs flex items-center gap-1 ml-auto"
          data-testid="backfill-mark-all-btn">
          <Save size={14} />
          {busyId === "markall" ? "Marking…" : "Mark all Full"}
        </button>
      </div>

      {loading ? <div className="text-sm text-neutral-500">Loading…</div> : (
        habits.length === 0 ? <div className="text-sm text-neutral-500">No habits active on this date.</div> :
        <div className="space-y-2">
          {habits.map((h) => {
            const st = h.log?.status;
            const pill = (val, label, activeBg, idleBg) => (
              <button key={val} type="button"
                onClick={() => setStatus(h, val)}
                disabled={busyId === h.id + val}
                className={`nb-btn text-xs px-3 py-2 ${st === val ? "pressed" : ""}`}
                style={{ background: st === val ? activeBg : idleBg }}
                data-testid={`backfill-${val}-${h.id}`}>
                {label}
              </button>
            );
            return (
              <div key={h.id} className="border-2 border-ink rounded-xl p-3 bg-white flex items-center justify-between gap-3 flex-wrap"
                   data-testid={`backfill-row-${h.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{h.name}</div>
                  <div className="text-[10px] font-mono text-neutral-500 mt-0.5">
                    Full {h.full}{h.partial > 0 ? ` · Partial ${h.partial}` : ""}
                    {st && <> · <span className="text-ink font-bold uppercase">{st}</span> = {h.log.points}</>}
                    {!st && <> · <span className="italic text-neutral-400">unlogged</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap">
                  {pill("full", `Full · ${h.full}`, "#10B981", "#D1FAE5")}
                  {h.partial > 0 && pill("partial", `Partial · ${h.partial}`, "#FACC15", "#FEF3C7")}
                  {pill("zero", "Zero", "#F43F5E", "#FECDD3")}
                  {st && (
                    <button type="button" onClick={() => clearLog(h)}
                            disabled={busyId === h.id + "clear"}
                            className="nb-btn bg-white text-xs px-2 py-2"
                            data-testid={`backfill-clear-${h.id}`}
                            title="Clear this log">
                      <XIcon size={12} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Page ----------
export default function Admin() {
  const [owner, setOwner] = useState("adi");
  const [habits, setHabits] = useState([]);
  const [settings, setSettings] = useState(null);
  const [creating, setCreating] = useState({ ...emptyHabit });
  const [bonus, setBonus] = useState({ username: "adi", points: 0, reason: "" });
  const [manualBonuses, setManualBonuses] = useState([]);
  const [resetting, setResetting] = useState(false);

  async function loadAll() {
    try {
      const [h, s, m] = await Promise.all([
        http.get(`/admin/habits`, { params: { owner } }),
        http.get(`/admin/settings`),
        http.get(`/admin/bonus/${owner}`),
      ]);
      setHabits(h.data);
      setSettings(s.data);
      setManualBonuses(m.data);
    } catch (e) { toast.error(fmtErr(e)); }
  }
  useEffect(() => { loadAll(); }, [owner]);

  async function saveHabit(h) {
    try {
      await http.patch(`/admin/habits/${h.id}`, {
        name: h.name, full: Number(h.full),
        partial: Number(h.partial), partial_label: h.partial_label, days: h.days, position: Number(h.position),
      });
      toast.success("Saved");
      loadAll();
    } catch (e) { toast.error(fmtErr(e)); }
  }
  async function delHabit(id) {
    if (!window.confirm("Delete this habit? All logs will be lost.")) return;
    try { await http.delete(`/admin/habits/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(fmtErr(e)); }
  }
  async function createHabit() {
    if (!creating.name.trim()) return toast.error("Name required");
    try {
      await http.post(`/admin/habits`, { ...creating, owner, full: Number(creating.full), partial: Number(creating.partial) });
      toast.success("Habit created");
      setCreating({ ...emptyHabit, owner });
      loadAll();
    } catch (e) { toast.error(fmtErr(e)); }
  }
  async function saveSettings() {
    try {
      await http.patch(`/admin/settings`, { start_date: settings.start_date, end_date: settings.end_date });
      toast.success("Challenge dates updated");
    } catch (e) { toast.error(fmtErr(e)); }
  }
  async function addBonus() {
    if (!bonus.reason.trim()) return toast.error("Reason required");
    try {
      await http.post(`/admin/bonus`, { username: bonus.username, points: Number(bonus.points), reason: bonus.reason });
      toast.success("Bonus added");
      setBonus({ ...bonus, points: 0, reason: "" });
      if (bonus.username === owner) loadAll();
    } catch (e) { toast.error(fmtErr(e)); }
  }
  async function delBonus(id) {
    try { await http.delete(`/admin/bonus/${id}`); toast.success("Removed"); loadAll(); }
    catch (e) { toast.error(fmtErr(e)); }
  }
  async function resetChallenge() {
    const first = window.prompt('Reset wipes ALL completion logs and bonuses for BOTH users. Type "RESET" to confirm.');
    if (first !== "RESET") return;
    setResetting(true);
    try {
      const r = await http.post(`/admin/reset-challenge`);
      toast.success(`Reset done · ${r.data.logs_deleted} logs & ${r.data.bonuses_deleted} bonuses cleared`);
      loadAll();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setResetting(false); }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-ink border-2 border-ink shadow-[3px_3px_0_0_#171717] flex items-center justify-center">
          <ShieldCheck size={24} strokeWidth={2.5} color="#FBBF24" />
        </div>
        <h1 className="font-display font-black text-3xl">Admin Console</h1>
      </div>

      {/* Settings */}
      {settings && (
        <div className="nb-card p-6 mb-6">
          <h2 className="font-display font-black text-xl mb-4">Challenge Window</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs uppercase font-mono tracking-widest">Start</label>
              <input type="date" className="nb-input mt-1 block" value={settings.start_date} onChange={(e) => setSettings({ ...settings, start_date: e.target.value })} data-testid="settings-start" />
            </div>
            <div>
              <label className="text-xs uppercase font-mono tracking-widest">End</label>
              <input type="date" className="nb-input mt-1 block" value={settings.end_date} onChange={(e) => setSettings({ ...settings, end_date: e.target.value })} data-testid="settings-end" />
            </div>
            <button className="nb-btn bg-ink text-white px-4 py-3" onClick={saveSettings} data-testid="save-settings"><Save size={16} className="inline mr-2" /> Save</button>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="nb-card p-6 mb-6 border-rose-500" style={{ background: "#FFF1F2" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-black text-xl mb-1 flex items-center gap-2">
              <RotateCcw size={20} /> Reset Challenge
            </h2>
            <p className="text-sm text-neutral-700 max-w-xl">
              Wipes every completion log & bonus for both Adi and Kudrat so the board goes to 0–0.
              Habits, users, dates and admin bonuses history remain intact.
            </p>
          </div>
          <button className="nb-btn bg-rose-500 text-white px-4 py-3 flex items-center gap-2 shrink-0"
                  onClick={resetChallenge} disabled={resetting} data-testid="reset-challenge-btn">
            <RotateCcw size={16} /> {resetting ? "Resetting…" : "Reset to 0–0"}
          </button>
        </div>
      </div>

      {/* Backfill past logs */}
      <BackfillLogs />

      {/* Owner switch */}
      <div className="flex gap-2 mb-4">
          <button onClick={() => setOwner("adi")} className={`nb-btn px-4 py-2 ${owner === "adi" ? "bg-adi text-white" : "bg-white"}`} data-testid="admin-tab-adi">Adi habits</button>
        <button onClick={() => setOwner("kudrat")} className={`nb-btn px-4 py-2 ${owner === "kudrat" ? "bg-kudrat text-white" : "bg-white"}`} data-testid="admin-tab-kudrat">Kudrat habits</button>
      </div>

      {/* Habits list */}
      <div className="nb-card p-6 mb-6">
        <h2 className="font-display font-black text-xl mb-4">Habits for {owner.toUpperCase()}</h2>
        <div className="space-y-4">
          {habits.map((h, idx) => (
            <div key={h.id} className="border-2 border-ink rounded-xl p-4 bg-neutral-50" data-testid={`admin-habit-${h.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                <div className="md:col-span-4">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Name</label>
                  <input className="nb-input w-full mt-1" value={h.name}
                         onChange={(e) => setHabits(habits.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Full pts</label>
                  <input className="nb-input w-full mt-1" type="number" step="0.5" value={h.full}
                         onChange={(e) => setHabits(habits.map((x, i) => i === idx ? { ...x, full: e.target.value } : x))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Partial pts</label>
                  <input className="nb-input w-full mt-1" type="number" step="0.5" value={h.partial}
                         onChange={(e) => setHabits(habits.map((x, i) => i === idx ? { ...x, partial: e.target.value } : x))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Frequency</label>
                  <div className="mt-1">
                    <FrequencyPicker value={h.days}
                                     onChange={(v) => setHabits(habits.map((x, i) => i === idx ? { ...x, days: v } : x))}
                                     testPrefix={`habit-${h.id}`} />
                  </div>
                </div>
                <div className="md:col-span-2 flex gap-2 md:pt-6">
                  <button className="nb-btn bg-emerald-300 flex-1 py-2 text-xs" onClick={() => saveHabit(h)} data-testid={`save-habit-${h.id}`}><Save size={14} className="inline" /></button>
                  <button className="nb-btn bg-rose-300 flex-1 py-2 text-xs" onClick={() => delHabit(h.id)} data-testid={`delete-habit-${h.id}`}><Trash2 size={14} className="inline" /></button>
                </div>
                <div className="md:col-span-12 text-xs text-neutral-500 font-mono uppercase tracking-widest">Description &amp; notes are owned by the user; admin cannot edit them.</div>
              </div>
            </div>
          ))}
        </div>

        {/* Create new */}
        <div className="mt-6 pt-6 border-t-2 border-dashed border-ink">
          <h3 className="font-display font-bold text-lg mb-3">Add a new habit</h3>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
            <div className="md:col-span-4">
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Name</label>
              <input className="nb-input w-full mt-1" placeholder="e.g. Meditation" value={creating.name}
                     onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="new-habit-name" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Full pts</label>
              <input className="nb-input w-full mt-1" type="number" step="0.5" placeholder="Full" value={creating.full}
                     onChange={(e) => setCreating({ ...creating, full: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Partial pts</label>
              <input className="nb-input w-full mt-1" type="number" step="0.5" placeholder="Partial" value={creating.partial}
                     onChange={(e) => setCreating({ ...creating, partial: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Frequency</label>
              <div className="mt-1">
                <FrequencyPicker value={creating.days}
                                 onChange={(v) => setCreating({ ...creating, days: v })}
                                 testPrefix="new" />
              </div>
            </div>
            <button className="nb-btn bg-gold md:col-span-2 py-2 md:mt-6" onClick={createHabit} data-testid="add-habit-btn"><Plus size={16} className="inline mr-1" /> Add</button>
            <div className="md:col-span-12 text-xs text-neutral-500 font-mono uppercase tracking-widest">The user will add the description and notes themselves.</div>
          </div>
        </div>
      </div>

      {/* Manual bonus */}
      <div className="nb-card p-6">
        <h2 className="font-display font-black text-xl mb-4">Manual Bonuses / Penalties</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
          <select className="nb-input md:col-span-2" value={bonus.username} onChange={(e) => setBonus({ ...bonus, username: e.target.value })}>
            <option value="adi">Adi</option>
            <option value="kudrat">Kudrat</option>
          </select>
          <input className="nb-input md:col-span-2" type="number" step="1" placeholder="Points (+/-)" value={bonus.points} onChange={(e) => setBonus({ ...bonus, points: e.target.value })} data-testid="bonus-points" />
          <input className="nb-input md:col-span-6" placeholder="Reason" value={bonus.reason} onChange={(e) => setBonus({ ...bonus, reason: e.target.value })} data-testid="bonus-reason" />
          <button className="nb-btn bg-adi text-white md:col-span-2 py-2" onClick={addBonus} data-testid="add-bonus-btn"><Plus size={16} className="inline mr-1" /> Award</button>
        </div>

        <div>
          <h3 className="font-display font-bold text-sm uppercase tracking-widest mb-2">History for {owner}</h3>
          {manualBonuses.length === 0 ? <div className="text-neutral-500 text-sm">No manual entries.</div> : (
            <ul className="space-y-2">
              {manualBonuses.map((b) => (
                <li key={b.id} className="flex items-center justify-between border-b border-dashed border-neutral-300 py-2 gap-2">
                  <div>
                    <div className="font-semibold">{b.reason}</div>
                    <div className="text-xs font-mono text-neutral-500">{b.date}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`chip ${b.points >= 0 ? "bg-emerald-100" : "bg-rose-100"}`}>{b.points >= 0 ? "+" : ""}{b.points}</span>
                    <button className="nb-btn bg-rose-300 px-2 py-1 text-xs" onClick={() => delBonus(b.id)}><Trash2 size={12} /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
